import { httpsCallable } from 'firebase/functions';
import { normalizeError } from '../../core/errors/index';
import { getTraceContext, newSpanId } from '../audit/traceContext';
import { DATA_ADAPTER } from '../../config/adapters';
import { useFirebaseEmulators } from '../../config/env';
import { ensureAppCheckReady } from './AppCheckService';
import { invokeWithCallableTimeout, timeoutMsForCallable } from './callFunctionTimeout';

export type Payload = Record<string, unknown>;

export type CallableData = Payload | object;

async function getFunctionsInstance() {
  const { functions } = await import('../../config/firebase.js');
  return functions;
}

async function prepareCallableInvocation() {
  if (DATA_ADAPTER === 'firebase' && !useFirebaseEmulators()) {
    await ensureAppCheckReady();
  }
}

export async function callFunction(name: string, data: CallableData = {}): Promise<unknown> {
  const { traceId } = getTraceContext();
  try {
    const result = await invokeWithCallableTimeout((async () => {
      await prepareCallableInvocation();
      const functions = await getFunctionsInstance();
      const fn = httpsCallable(functions, name);
      return fn(data as Payload);
    })(), timeoutMsForCallable(name));
    return result.data;
  } catch (err: unknown) {
    throw normalizeError(err, {
      operation: name,
      traceId,
      spanId: newSpanId(name),
    });
  }
}

export async function healthCheck() {
  return callFunction('health');
}

export async function setUserRoleClaims(payload: {
  uid: string;
  role: string;
  houseId?: string;
}) {
  return callFunction('setUserRole', payload);
}

export async function provisionStaffUser(payload: CallableData) {
  return callFunction('provisionStaffUser', payload);
}

export async function deleteStaffUserCallable(payload: CallableData) {
  return callFunction('deleteStaffUser', payload);
}

export async function createHouseCallable(payload: CallableData) {
  return callFunction('createHouse', payload);
}

export async function getGodOnboarding() {
  return callFunction('getGodOnboarding');
}

export async function updateGodOnboarding(patch: CallableData) {
  return callFunction('updateGodOnboarding', { patch });
}

export async function syncRegisteredProfile() {
  return callFunction('syncRegisteredProfile');
}

export async function sendEmailMfaCode(purpose: 'enroll' | 'signin' = 'enroll') {
  return callFunction('sendEmailMfaCode', { purpose });
}

export async function verifyEmailMfaEnrollment(code: string) {
  return callFunction('verifyEmailMfaEnrollment', { code });
}

export async function verifyEmailMfaSignIn(code: string) {
  return callFunction('verifyEmailMfaSignIn', { code });
}

export async function getEmailMfaStatus() {
  return callFunction('getEmailMfaStatus');
}

export async function disableEmailMfa() {
  return callFunction('disableEmailMfa');
}

export async function dismissStaffGoogleLinkOnboarding() {
  return callFunction('dismissStaffGoogleLinkOnboarding');
}

export async function acceptTermsOfService(payload: { version: string }) {
  return callFunction('acceptTermsOfService', payload);
}

export async function submitAccessRequest(payload: CallableData) {
  return callFunction('submitAccessRequest', payload);
}

export async function requestPasswordReset(payload: { email: string }) {
  return callFunction('requestPasswordReset', payload);
}

export async function startMfaRecovery(payload: {
  usernameOrEmail: string;
  password: string;
}) {
  return callFunction('startMfaRecovery', payload);
}

export async function confirmMfaRecovery(payload: {
  usernameOrEmail: string;
  password: string;
  code: string;
}) {
  return callFunction('confirmMfaRecovery', payload);
}

export async function requestMfaResetSupport(payload: {
  usernameOrEmail: string;
  password: string;
  message?: string;
}) {
  return callFunction('requestMfaResetSupport', payload);
}

export async function adminClearUserMfa(payload: { uid?: string; email?: string }) {
  return callFunction('adminClearUserMfa', payload);
}

export async function resolveLoginEmail(payload: { username: string }) {
  return callFunction('resolveLoginEmail', payload);
}

export async function checkUsernameAvailable(payload: { username: string }) {
  return callFunction('checkUsernameAvailable', payload);
}

export async function updateOwnUsername(payload: { username: string }) {
  return callFunction('updateOwnUsername', payload);
}

export async function startOwnEmailChange(payload: { kind: 'primary' | 'alt'; email: string }) {
  return callFunction('startOwnEmailChange', payload);
}

export async function confirmOwnEmailChange(payload: {
  kind: 'primary' | 'alt';
  code: string;
}) {
  return callFunction('confirmOwnEmailChange', payload);
}

export async function clearOwnAltEmail() {
  return callFunction('clearOwnAltEmail');
}

export async function confirmOwnPhone(payload: { phone: string }) {
  return callFunction('confirmOwnPhone', payload);
}

export async function clearOwnPhone() {
  return callFunction('clearOwnPhone');
}

export async function restoreOwnAuthPhoneFromProfile() {
  return callFunction('restoreOwnAuthPhoneFromProfile');
}

export async function ensureOwnReferralCode() {
  return callFunction('ensureOwnReferralCode');
}

export async function ensureStaffReferralCode(payload: { userId: string }) {
  return callFunction('ensureStaffReferralCode', payload);
}

export async function backfillReferralCodes(payload: { limit?: number } = {}) {
  return callFunction('backfillReferralCodes', payload);
}

export async function sendPushNotification(payload: CallableData) {
  return callFunction('sendPushNotification', payload);
}

export async function sendDirectMessage(payload: CallableData) {
  return callFunction('sendDirectMessage', payload);
}

export async function sendHouseBroadcast(payload: CallableData) {
  return callFunction('sendHouseBroadcast', payload);
}

export async function refreshExchangeRates() {
  return callFunction('refreshExchangeRates');
}

export async function refreshCryptoRates(force = false) {
  return callFunction('refreshCryptoRates', { force });
}

export async function forceLotteryResultsSync(payload: {
  source: 'florida' | 'newyork';
  session: 'midday' | 'evening';
}) {
  return callFunction('forceLotteryResultsSync', payload);
}

export async function resolveHousePlatformResultConflict(payload: {
  houseId: string;
  dayKey: string;
  window: 'day' | 'night';
  resolution: 'sync_with_platform' | 'keep_house';
}) {
  return callFunction('resolveHousePlatformResultConflict', payload);
}

export async function publishPlatformLotteryResults(payload: {
  source: 'florida' | 'newyork';
  window: 'day' | 'night';
  dayKey: string;
  first: string;
  second: string;
  third: string;
  pick3?: string | null;
  pick4?: string | null;
  numbers?: string | null;
  win4?: string | null;
  cash3?: string | null;
  cash4?: string | null;
}) {
  return callFunction('publishPlatformLotteryResults', payload);
}

/** Ops-only: rebuild platform draws from house history (prefer first house). */
export async function hydratePlatformLotteryDraws(
  payload?: { dayKey?: string; houseId?: string },
) {
  return callFunction('hydratePlatformLotteryDraws', payload ?? {});
}

/** Ops-only: rewrite platform draws so Pick 3 · Pick 4 are stored explicitly. */
export async function backfillPlatformLotteryPicks(
  payload?: { dayKey?: string; limit?: number },
) {
  return callFunction('backfillPlatformLotteryPicks', payload ?? {});
}

export async function setPlatformLotteryYoutubeVideo(payload: {
  source: 'florida' | 'newyork';
  dayKey: string;
  window: 'day' | 'night';
  url: string;
}) {
  return callFunction('setPlatformLotteryYoutubeVideo', payload);
}

export async function createPlay(payload: CallableData) {
  return callFunction('createPlay', payload);
}

export async function optimizeHouseRoute(payload: CallableData) {
  return callFunction('optimizeHouseRoute', payload);
}

export async function getRouteDirections(payload: CallableData) {
  return callFunction('getRouteDirections', payload);
}

export async function reportRouteIdleAlert(payload: CallableData) {
  return callFunction('reportRouteIdleAlert', payload);
}

export async function geocodeAddress(payload: CallableData) {
  return callFunction('geocodeAddress', payload);
}

export async function updateHouseCallable(payload: CallableData) {
  return callFunction('updateHouse', payload);
}

export async function draftHouseRoute(payload: CallableData) {
  return callFunction('draftHouseRoute', payload);
}

export async function confirmHouseRoute(payload: CallableData) {
  return callFunction('confirmHouseRoute', payload);
}

export async function addAdhocRouteStop(payload: CallableData) {
  return callFunction('addAdhocRouteStop', payload);
}

export async function authorizeHouseRouteDeparture(payload: CallableData) {
  return callFunction('authorizeHouseRouteDeparture', payload);
}

export async function completeHouseRouteCollection(payload: CallableData) {
  return callFunction('completeHouseRouteCollection', payload);
}

export async function requestRouteStopSkip(payload: CallableData) {
  return callFunction('requestRouteStopSkip', payload);
}

export async function reviewRouteStopSkip(payload: CallableData) {
  return callFunction('reviewRouteStopSkip', payload);
}

export async function skipHouseRouteStop(payload: CallableData) {
  return callFunction('skipHouseRouteStop', payload);
}

export async function confirmListerRouteStop(payload: CallableData) {
  return callFunction('confirmListerRouteStop', payload);
}

export async function confirmRunnerRouteStop(payload: CallableData) {
  return callFunction('confirmRunnerRouteStop', payload);
}

export async function ensureHouseRouteAutoAuthorized(payload: CallableData = {}) {
  return callFunction('ensureHouseRouteAutoAuthorized', payload);
}
